import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Divider,
useTheme,
Menu
} from '@mui/material';
import { 
Delete, 
Search,
Add as AddIcon,
CloudUpload,
Close as CloseIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
PictureAsPdf,TableChart, GetApp
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { APIBrancheNamesFilter, APISupplierDelete, APISupplierList, APISupplierStore, APISupplierUpdate } from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import { formatCreatedAtDate, formatNumberWithComma, formatNumberWithComma2, getCategoriesNameById,
    getUserDetails, recordsPerPage } from '../../utils/helpers';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';
import { 
exportToExcel,
exportToPDF,
exportToCSV,
} from '../../services/units/SupplierService';
const Supplier = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        location: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        phone: '',
        email: '',
        location: '',
        dateFrom: '',
        dateTo: ''
    });
    const [errors, setErrors] = useState({
        name: false,
        phone: false,
        email: false,
        location: false,
    });
    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

    //  Permisions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);
    // Helper function
    const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        fetchData();
        fetchBranchNames();
    }, []);

    const fetchBranchNames = async () => {
        try {
            const response = await axios.get(APIBrancheNamesFilter);
            if (response.data.status === 'success') {
                setBranchNamesFilterData(response.data.data || []);
                showToast("Success", "Branch names loaded successfully", "success");
            }
        } catch (error) {
            console.error("Error fetching branch names:", error);
            showToast("Error", "Failed to load branch names", "error");
        }
    };

    const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
            // For super admin, use selected branch or current user's branch
            let branchIdToUse = branchId;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            const response = await axios.get(APISupplierList, { params });

            const suppliers = Array.isArray(response.data.data) ? response.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                pageData: suppliers
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";

            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }

            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBranchSelect = (branchId) => {
        setSelectedBranch(branchId);
        setBranchSelectionOpen(false);
        fetchData(branchId);
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
        record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.location.toLowerCase().includes(searchTerm.toLowerCase());

        // Field filters
        const matchesName = !filters.name ||
        record.name.toLowerCase().includes(filters.name.toLowerCase());
        const matchesPhone = !filters.phone ||
        record.phone.toLowerCase().includes(filters.phone.toLowerCase());
        const matchesEmail = !filters.email ||
        record.email.toLowerCase().includes(filters.email.toLowerCase());
        const matchesLocation = !filters.location ||
        record.location.toLowerCase().includes(filters.location.toLowerCase());

        // Date range filter
        let matchesDate = true;
        if (filters.dateFrom) {
        const crecordDate = new Date(record.created_at).toISOString().split('T')[0];
        const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
        matchesDate = matchesDate && crecordDate >= filterFromDate;
        }

        if (filters.dateTo) {
        const crecordDate = new Date(record.created_at).toISOString().split('T')[0];
        const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
        matchesDate = matchesDate && crecordDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesPhone && matchesEmail && matchesLocation && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        setPostStatus("ADD");
        setFormData({
            name: '',
            phone: '',
            email: '',
            location: ''
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        // Set form data
        setFormData({
            name: record.name,
            phone: record.phone,
            email: record.email,
            location: record.location
        });
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Reset error for this field
        setErrors(prev => ({
        ...prev,
        [name]: false
        }));

        // Field-specific validation
        if (name === 'phone') {
        // Check if the input is numeric or empty
        if (value === '' || /^[\d\s\-()+]*$/.test(value)) {
            setFormData(prev => ({
            ...prev,
            [name]: value
            }));
        } else {
            // Show error and don't update the field with non-numeric value
            setErrors(prev => ({
            ...prev,
            [name]: true
            }));
            showToast("Input Error", "Please enter valid phone characters only", "error");
            return;
        }
        } else {
        // For other fields
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
        ...prev,
        [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
        ...filters,
        dateFrom: today,
        dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
        name: '',
        phone: '',
        email: '',
        location: '',
        dateFrom: '',
        dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    // ============== Export handlers begin ========================
    const handleExportClick = (event) => {setExportAnchorEl(event.currentTarget);};
    const handleExportClose = () => {setExportAnchorEl(null);};

// In the export handlers section of Supplier.js, update these functions:

const handleExportExcel = async () => {
  try {
    handleExportClose();
    await exportToExcel(filteredData, branchNamesFilterData, {
      name: filters.name,
      phone: filters.phone,
      email: filters.email,
      location: filters.location,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo
    });
    showToast("Success", "Excel file downloaded successfully", "success");
  } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
  }
};

const handleExportPDF = async () => {
  try {
    handleExportClose();
    await exportToPDF(filteredData, branchNamesFilterData, {
      name: filters.name,
      phone: filters.phone,
      email: filters.email,
      location: filters.location,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo
    });
    showToast("Success", "PDF file downloaded successfully", "success");
  } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
  }
};

const handleExportCSV = async () => {
  try {
    handleExportClose();
    await exportToCSV(filteredData, branchNamesFilterData);
    showToast("Success", "CSV file downloaded successfully", "success");
  } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
  }
};
    // ============== Export handlers end ========================

    const validateForm = () => {
        const errors = [];
        if (!formData.name.trim()) errors.push('Name is required');
        if (!formData.phone.trim()) errors.push('Phone is required');
        if (!formData.email.trim()) errors.push('Email is required');
        if (!formData.location.trim()) errors.push('Location is required');

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            errors.push('Please enter a valid email address');
        }

        if (errors.length > 0) {
        showToast("Validation Error", errors.join('\n'), "warning");
        return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setFilterOptions(false);
    };

    const handleFormDataReset = () => {
        setFormData({
            name: '',
            phone: '',
            email: '',
            location: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (postStatus !== "DELETE") {
        if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('name', formData.name);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('location', formData.location);

        // Only append updateId for UPDATE/DELETE operations
        if (postStatus === "UPDATE" || postStatus === "DELETE") {
        formDataToSend.append('updateId', updateId);
        }

        try {
        let apiUrl, method;
        if (postStatus === "ADD") {
            apiUrl = APISupplierStore;
            method = 'post';
        } else if (postStatus === "UPDATE") {
            apiUrl = APISupplierUpdate;
            method = 'post';
        } else if (postStatus === "DELETE") {
            apiUrl = APISupplierDelete;
            method = 'post';
        }

        const response = await axios({
            method,
            url: apiUrl,
            data: formDataToSend});

        if (response.data.status === "success") {
            showToast("Success", response.data.message, "success");
            handleFormDataReset();
            handleDialogClose();
            fetchData();
        } else {
            // Handle unexpected success response without data
            showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
        }
        } catch (error) {
        if (error.response) {
            // Server responded with a status code outside 2xx
            const { status, data } = error.response;
            if (status === 422) {
            // Validation errors
            const errors = data.errors || {};
            let errorMessages = [];
            // Format validation errors for display
            for (const field in errors) {
                errorMessages.push(`${field}: ${errors[field].join(', ')}`);
            }
            showToast(
                "Validation Error",
                errorMessages.join('\n') || "Please correct the form errors",
                "error"
            );
            } else if (status === 409) {
            // Conflict - duplicate entry
            showToast("Error", data.message || "This record already exists", "error");
            } else if (status >= 500) {
            // Server errors
            showToast("Server Error", data.message || "Internal server error", "error");
            } else {
            // Other 4xx errors
            showToast("Error", data.message || "Request failed", "error");
            }
        } else if (error.request) {
            // Request was made but no response received
            showToast("Network Error", "No response from server. Please check your connection.", "error");
        } else {
            // Something happened in setting up the request
            showToast("Error", error.message || "An unexpected error occurred", "error");
        }
        // Log full error for debugging
        console.error("API Error:", error);
        }
    };
    const getSelectedBranchName = () => {
        if (!selectedBranch) return "Select Branch";
        const branch = branchNamesFilterData.find(b => b.id === selectedBranch);
        return branch ? `${branch.name} (${branch.code})` : "Select Branch";
    };


    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Title - full width on mobile */}
        <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
        }}>
        Suppliers ({formatNumberWithComma2(pageState.pageData.length)})
        </Typography>
        
        {/* Display selected branch name */}
        {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
            <Box sx={{ mt: 1, mb: 2 }}>
                <Chip
                    label={`Selected Branch: ${getSelectedBranchName()}`}
                    color="primary"
                    variant="filled"
                    onDelete={() => {
                        setSelectedBranch('');
                        fetchData();
                    }}
                    deleteIcon={<CloseIcon />}
                />
            </Box>
        )}

        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                flexWrap: 'wrap' 
            }}>

                {/* Buttons - column on mobile, row on desktop */}
                <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexShrink: 0,
                order: { xs: 2, sm: 1 }
                }}>
                {hasPermission(PERMISSIONS.ADD) &&(
                    <Button
                    variant="contained"
                    color="warning"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAdd}
                    sx={{
                    textTransform: 'none',
                    borderRadius: '8px'
                    }}
                    size="small"
                >
                    Add Supplier
                </Button>
                )}

                {hasPermission(PERMISSIONS.BRANCHES) && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<FilterIcon />}
                        onClick={() => setBranchSelectionOpen(true)}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px'
                        }}
                        size="small"
                    >
                        Select Branch
                    </Button>
                )}
                
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<FilterIcon />}
                    onClick={handlefilterOptions}
                    sx={{
                    textTransform: 'none',
                    borderRadius: '8px'
                    }}
                    size="small"
                >
                    Filter
                </Button>
                
                {hasPermission(PERMISSIONS.EXPORT) && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<GetApp />}
                        onClick={handleExportClick}
                        sx={{
                        textTransform: 'none',
                        borderRadius: '8px'
                        }}
                        size="small"
                    >
                        Export Data
                    </Button>
                )}
                </Box>

                {/* Search Bar - full width on mobile, fixed on desktop */}
                <Paper sx={{
                width: { xs: '100%', sm: '300px' },
                p: '2px 8px',
                display: 'flex',
                alignItems: 'center',
                marginLeft: { xs: 0, sm: 'auto' },
                order: { xs: 1, sm: 2 },
                borderRadius: '8px',
                background: theme.palette.primary.light
                }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search suppliers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                    sx: {
                        '& fieldset': { border: 'none' },
                        fontSize: '0.875rem',
                        color: 'white',
                        '& input': {
                        color: 'white',
                        '&::placeholder': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            opacity: 1
                        }
                        }
                    }
                    }}
                    size="small"
                />
                </Paper>
            </Box>

            {/* Active Filters as a separate row below */}
            {(filters.name || filters.phone || filters.email || filters.location || filters.dateFrom || filters.dateTo) && (
                <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
                }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Active filters:
                </Typography>
                {filters.name && (
                    <Chip
                    label={`Name: ${filters.name}`}
                    onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    />
                )}
                {filters.phone && (
                    <Chip
                    label={`Phone: ${filters.phone}`}
                    onDelete={() => setFilters(prev => ({ ...prev, phone: '' }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    />
                )}
                {filters.email && (
                    <Chip
                    label={`Email: ${filters.email}`}
                    onDelete={() => setFilters(prev => ({ ...prev, email: '' }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    />
                )}
                {filters.location && (
                    <Chip
                    label={`Location: ${filters.location}`}
                    onDelete={() => setFilters(prev => ({ ...prev, location: '' }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    />
                )}
                {filters.dateFrom && (
                    <Chip
                    label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                    onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    />
                )}
                {filters.dateTo && (
                    <Chip
                    label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                    onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    />
                )}
                <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={<CloseIcon fontSize="small" />}
                    sx={{ minWidth: 'auto' }}
                >
                    Clear All
                </Button>
                </Box>
            )}
        </Paper>

        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                <TableHead>
                    <TableRow sx={{
                    backgroundColor: theme.palette.primary.main,
                    '& th': {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.95rem'
                    }
                    }}>
                    <TableCell>#</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Location</TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                    <TableCell align="center">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((supplier, index) => (
                    <TableRow
                        key={index}
                        hover
                        sx={{
                        '&:nth-of-type(odd)': {
                            backgroundColor: theme.palette.action.hover
                        }
                        }}
                    >
                        <TableCell>
                        <Chip
                            label={page * rowsPerPage + index + 1}
                            color="primary"
                            size="small"
                        />
                        </TableCell>
                        <TableCell>{formatCreatedAtDate(supplier.created_at)}</TableCell>
                        <TableCell sx={{ fontWeight: 'medium' }}>
                        {supplier.name}
                        </TableCell>
                        <TableCell>
                        <Chip
                            label={supplier.phone}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                        <TableCell>
                        <Chip
                            label={supplier.email}
                            color="success"
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                        <TableCell>
                        <Chip
                            label={supplier.location}
                            color="warning"
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                        {hasPermission(PERMISSIONS.BRANCHES) && (
                            <TableCell>
                                <Chip
                                    label={getCategoriesNameById(supplier.branch_id,branchNamesFilterData)}
                                    color="info"
                                    variant="outlined"
                                    size="small"
                                />
                            </TableCell>
                        )}
                        <TableCell align="center">
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenView(supplier)}
                            color="primary"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.info.light
                            }
                            }}
                        >
                            <ViewIcon />
                        </IconButton>
                        {hasPermission(PERMISSIONS.EDIT)&&(<>
                            <IconButton
                            edge="end"
                            onClick={() => handleOpenEdit(supplier)}
                            color="secondary"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.success.light
                                }
                                }}
                            >
                                <EditIcon />
                            </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                            <IconButton
                                edge="end"
                                onClick={() => handleOpenDelete(supplier)}
                                color="error"
                                sx={{
                                '&:hover': {
                                    backgroundColor: theme.palette.error.light
                                }
                                }}
                            >
                                <Delete />
                            </IconButton>
                        </>)}
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
                </Table>
            </TableContainer>
            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ mt: 2 }}
            />

            {filteredData.length === 0 && (
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 8,
                color: 'text.secondary'
                }}>
                <Search sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6">No suppliers found</Typography>
                <Typography variant="body2">Try adjusting your search query or select a branch</Typography>
                </Box>
            )}
            </Paper>
        </motion.div>

        {/* Branch Selection Dialog */}
        <Dialog
            open={branchSelectionOpen}
            onClose={() => setBranchSelectionOpen(false)}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel id="branch-select-label">Select Branch</InputLabel>
                        <Select
                            labelId="branch-select-label"
                            value={selectedBranch}
                            label="Select Branch"
                            onChange={(e) => handleBranchSelect(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Select a branch</em>
                            </MenuItem>
                            {branchNamesFilterData.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name} ({branch.code})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
                        <strong>Note:</strong> Selecting a branch will filter suppliers only from that branch. 
                        Leave unselected to view all suppliers.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    color="error"
                    onClick={() => setBranchSelectionOpen(false)}
                    variant="contained"
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        if (selectedBranch) {
                            fetchData(selectedBranch);
                        }
                        setBranchSelectionOpen(false);
                    }}
                    color="primary"
                    variant="contained"
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>

        {/* Export Menu */}
        <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                <TableChart fontSize="small" color="success" />
                Export to Excel (.xlsx)
            </MenuItem>
            <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                <PictureAsPdf fontSize="small" color="error" />
                Export to PDF (.pdf)
            </MenuItem>
            <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                <TableChart fontSize="small" color="info" />
                Export to CSV (.csv)
            </MenuItem>
        </Menu>

        {/* Options Dialog */}
        <Dialog
            open={filterOptions}
            onClose={handleCloseOptions}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
            <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={clearFilters}
                sx={{ textTransform: 'none' }}
                >
                Show All Suppliers
                </Button>

                <Button
                variant="outlined"
                color="primary"
                startIcon={<TodayIcon />}
                onClick={applyTodayFilter}
                sx={{ textTransform: 'none' }}
                >
                Show Today's Suppliers
                </Button>

                <Divider>OR</Divider>

                <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                Filter by Fields
                </Typography>

                <TextField
                fullWidth
                label="Name"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                />

                <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={filters.phone}
                onChange={handleFilterChange}
                />

                <TextField
                fullWidth
                label="Email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                />

                <TextField
                fullWidth
                label="Location"
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
                />

                <Divider>OR</Divider>

                <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                Filter by Date Range
                </Typography>

                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    name="dateFrom"
                    value={filters.dateFrom}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    name="dateTo"
                    value={filters.dateTo}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                </Grid>
            </Box>
            </DialogContent>
            <DialogActions>
            <Button
                color="error"
                onClick={handleCloseOptions}
                variant="contained"
            >
                Close
            </Button>
            <Button
                onClick={handleCloseOptions}
                color="primary"
                variant="contained"
            >
                Apply Filters
            </Button>
            </DialogActions>
        </Dialog>

        {/* Add/Edit Dialog */}
        <Dialog
            open={openAdd}
            onClose={handleCloseAdd}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="sm"
            scroll="paper"
        >
            <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "Add New Supplier" : "Edit Supplier"}</b></DialogTitle>
            <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Box>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Supplier Name"
                    name="name"
                    value={formData.name}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.name}
                    helperText={errors.name ? "Invalid input" : ""}
                    required
                    />
                </Grid>
                <br></br>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.phone}
                    helperText={errors.phone ? "Valid phone characters only" : ""}
                    required
                    />
                </Grid>
                <br></br>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.email}
                    helperText={errors.email ? "Invalid email" : ""}
                    required
                    />
                </Grid>
                <br></br>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={formData.location}
                    autoComplete="off"
                    onChange={handleInputChange}
                    error={errors.location}
                    helperText={errors.location ? "Invalid input" : ""}
                    required
                    />
                </Grid>
                </Box>
                <DialogActions sx={{ mt: 3, px: 0 }}>
                <Button
                    color="error"
                    onClick={handleCloseAdd}
                    variant="contained"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                >
                    {postStatus === "ADD" ? "Add Supplier" : "Update Supplier"}
                </Button>
                </DialogActions>
            </Box>
            </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Supplier Details</b></DialogTitle>
        <DialogContent>
            {selectedRecord && (
            <Box sx={{ p: 2 }}>
                <Box>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2"><b>Name: </b>{selectedRecord.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2"><b>Phone: </b>
                        <Chip
                            label={selectedRecord.phone}
                            color="primary"
                            variant="outlined"
                        />
                        </Typography>
                    </Grid>
                    <br></br>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2"><b>Email: </b>
                        <Chip
                            label={selectedRecord.email}
                            color="success"
                            variant="outlined"
                        />
                        </Typography>
                    </Grid>
                    <br></br>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2"><b>Location: </b>
                        <Chip
                            label={selectedRecord.location}
                            color="warning"
                            variant="outlined"
                        />
                        </Typography>
                    </Grid>
                </Box>
                <br></br>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                    <Typography variant="subtitle2">
                        <b>Branch: </b>
                        <Chip
                            label={selectedRecord.branch_name || 'N/A'}
                            color="info"
                            variant="outlined"
                        />
                    </Typography>
                )}
                <br></br>
                <Typography variant="subtitle2"><b>Created Date: </b>{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
            </Box>
            )}
        </DialogContent>
        <DialogActions>
            <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
            >
            Close
            </Button>
        </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
            open={openDelete}
            onClose={handleCloseDelete}
            TransitionComponent={Transition}
        >
            <DialogTitle sx={{ textAlign: 'center' }}>Delete Supplier</DialogTitle>
            <DialogContent>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                Are you sure you want to delete this supplier?
            </Typography>
            {selectedRecord && (
                <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                {selectedRecord.name} <br /> {selectedRecord.email}
                </Typography>
            )}
            </DialogContent>
            <DialogActions>
            <Button
                color="error"
                onClick={handleCloseDelete}
                variant="contained"
            >
                Cancel
            </Button>
            <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
            >
                Confirm Delete
            </Button>
            </DialogActions>
        </Dialog>
        </Container>
    );
};

export default Supplier;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});

